import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import StyledModal from "styled-react-modal";
import { CellContext } from "@tanstack/react-table";

import { FilterParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { ITableInstance } from "src/library/components/Tables/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { setUser } from "src/core/users/store/users.actions";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  ERPRJL,
  ERPSeedless
} from "src/utils/constants";
import { IStore } from "src/core/shared/store";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { LikeToDo } from "src/library/components/LikeToDo";
import { CustomerUserExportModal } from "src/core/customers/components/CustomerUserExportModal";
import { getUserTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import { Table } from "src/core/dashboard/components/widgets";

export interface MoveUserModalData {
  customer: ICustomer;
  drop: IDrop;
  userId: number;
}

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

/**
 * Main customer users view
 *
 * @returns
 */
const CustomersUsersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology, history } = props;
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ usersResponse, getUsers ] = useUserApi("LIST_USERS");
  const [ showImportModal, setShowImportModal ] = React.useState(false);
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const checkPermission = useCheckUserPermission();
  const dispatch = useDispatch();
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const updateUserSuccessMessage = `The ${terminology.user.singular.text} has been successfully updated.`;
  const baseUpdateUserErrorMessage = `Could not update ${terminology.user.singular.text}`;

  const onFetchData = React.useCallback((params: {filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>}) => {
    getUsers({
      pagination: params.pagination,
      filters: {
        ...params.filters,
        defaultCustomerId: { $not: null }
      }
    });
  }, [ getUsers ]);

  const canCreateUsers = React.useMemo(() => {
    return checkPermission("create-user");
  }, [ checkPermission ]);

  const resendInviteUserAction = React.useCallback((id: number) => {
    resendInvite({ id });
  }, [ resendInvite ]);

  const handleSuspendUser = React.useCallback((user: any) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "suspended" : "active"
    };

    suspendUser(updatedUser);
  }, [ suspendUser ]);

  // Handle suspend user update
  React.useEffect(() => {
    if (suspendUserResponse.data) {
      toast.success(updateUserSuccessMessage);
    }

    if (suspendUserResponse.error) {
      toast.error(`${baseUpdateUserErrorMessage} - ${suspendUserResponse.error.message}`);
    }
  }, [
    baseUpdateUserErrorMessage,
    suspendUserResponse,
    terminology.user.singular.text,
    updateUserSuccessMessage
  ]);

  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse ]);

  const ImportExportUsers = React.useCallback(() => {
    const buttons = [
      <Button
        key="export-customer-user"
        onClick={() => {
          setShowExportModal(true);
        }}
      >
        {`Export ${terminology.user.plural.text}`}

      </Button>
    ];

    if (erp === ERPSeedless || erp === ERPRJL) {
      buttons.push((
        <Button
          key="import-users"
          onClick={() => setShowImportModal(true)}
        >
          {`Import ${terminology.user.plural.text}`}
        </Button>)
      );
    }

    return buttons;
  }, [ erp, terminology ]);

  const userTableActionsButton = React.useCallback((info: CellContext<IUser, unknown>) => {
    const canAmendUser = checkPermission("amend-user");

    if (!canAmendUser) {
      return null;
    }

    const items = [
      {
        title: "Edit details",
        action: () => {
          dispatch(setUser(info.row.original));
          history.push(`/admin/customers/users/${info.row.original.id}/details`);
        }
      },
      {
        title: `Edit ${terminology.drop.plural.text}`,
        action: () => {
          dispatch(setUser(info.row.original));
          history.push(`/admin/customers/users/${info.row.original.id}/drops`);
        }
      },
      {
        title: `Edit ${terminology.customer.plural.text}`,
        action: () => {
          dispatch(setUser(info.row.original));
          history.push(`/admin/customers/users/${info.row.original.id}/customers`);
        }
      },
      {
        title: `${info.row.original.status === "active" ? "Suspend" : "Activate"}`,
        action: () => {
          handleSuspendUser(info.row.original);
        }
      }
    ];

    if (info.row.original.lastLoggedIn === null) {
      items.push({
        title: "Resend invite",
        action: () => resendInviteUserAction(info.row.original.id)
      });
    }

    return (
      <ActionButton
        title="Actions"
        items={items}
      />
    );
  }, [
    checkPermission,
    dispatch,
    handleSuspendUser,
    history,
    resendInviteUserAction,
    terminology.customer.plural.text,
    terminology.drop.plural.text
  ]);

  const columns = React.useMemo(() => {
    return getUserTableColumns({
      terminology,
      actionButton: userTableActionsButton
    });
  }, [ terminology, userTableActionsButton ]);

  return (
    <Layout
      {...props}
      title={`Customer ${terminology.user.plural.text}`}
    >
      <MainContentWrapper
        fill
        Header={canCreateUsers && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={ImportExportUsers()}
          />
        )}
      >

        <Table
          ref={TableRef}
          mainAction={row => history.push(`/admin/customers/users/${row.original.id}/details`)}
          error={usersResponse.error}
          tableOf={terminology.user.plural.text}
          loading={usersResponse.pending}
          data={usersResponse.data?.result ? usersResponse.data?.result : []}
          onFetchData={onFetchData}
          rowCount={usersResponse?.data?.count ?? 0}
          pageCount={usersResponse.data?.pages ?? 0}
          defaultPagination={defaultPagination}
          columns={columns}
        />

        <StyledModal isOpen={showImportModal}>
          <ImportModal
            open={showImportModal}
            onDismiss={() => setShowImportModal(false)}
            type="user"
            terminology={props.terminology}
          />
        </StyledModal>

        <CustomerUserExportModal
          open={showExportModal}
          onDismiss={() => {
            setShowExportModal(false);
          }}
        />

      </MainContentWrapper>
    </Layout>
  );
};

export default CustomersUsersPage;
